import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Sparkles, Loader2, PlugZap, DownloadCloud, CheckCircle2, XCircle, Cpu, Zap } from "lucide-react";

export default function WebhookSettings() {
  const qc = useQueryClient();
  const [testResult, setTestResult] = useState(null);
  const [pullLimit, setPullLimit] = useState(20);
  const [pullFilter, setPullFilter] = useState("");

  const q = useQuery({
    queryKey: ["webhook-config"],
    queryFn: () => api.get("/webhook-config").then(r => r.data),
  });
  const full = q.data ? `${BACKEND_URL}${q.data.endpoint_path}` : "";
  const apiConfigured = !!q.data?.syften_api_configured;
  const aiQ = useQuery({
    queryKey: ["ai-settings"],
    queryFn: () => api.get("/settings/ai").then(r => r.data),
  });
  const saveAI = useMutation({
    mutationFn: (body) => api.patch("/settings/ai", body),
    onSuccess: () => {
      toast.success("AI routing updated");
      qc.invalidateQueries({ queryKey: ["ai-settings"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update AI routing"),
  });

  const copy = () => {
    navigator.clipboard.writeText(full);
    toast.success("Webhook URL copied");
  };

  const karma = useMutation({
    mutationFn: () => api.post("/karma/generate"),
    onSuccess: (r) => toast.success(`Assigned ${r.data.created} drift tasks`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const testSyften = useMutation({
    mutationFn: () => api.post("/syften/test"),
    onSuccess: (r) => { setTestResult(r.data); toast.success("Syften API connected"); },
    onError: (e) => { setTestResult({ error: e?.response?.data?.detail || "Connection failed" }); toast.error("Syften API test failed"); },
  });

  const pullSyften = useMutation({
    mutationFn: () => {
      const body = { limit: pullLimit };
      if (pullFilter.trim()) body.filter = pullFilter.trim();
      return api.post("/syften/pull", body);
    },
    onSuccess: (r) => {
      const { total_returned = 0, accepted = 0, archived = 0, duplicate = 0 } = r.data;
      toast.success(`Pulled ${total_returned} · ${accepted} accepted · ${archived} archived · ${duplicate} dup`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Pull failed"),
  });

  const toggleModel = (modelId) => {
    const current = new Set(aiQ.data?.enabled_models || []);
    if (current.has(modelId)) current.delete(modelId);
    else current.add(modelId);
    saveAI.mutate({ enabled_models: [...current] });
  };

  return (
    <Layout title="Webhook & API." subtitle="AI routing · Syften webhook · REST API · Drift protocol">
      {/* AI model routing */}
      <div className="border border-white/10 p-6 mb-6" data-testid="ai-model-routing">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">// ai model routing</div>
            <h3 className="font-display font-black text-2xl tracking-tight">Choose which models may run.</h3>
            <p className="text-[13px] text-zinc-400 max-w-2xl mt-2">
              Enabled models are tried in speed-aware order. If one provider is busy or out of quota, ThreadOps falls back to the next enabled model.
            </p>
          </div>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <Cpu className="w-4 h-4 text-[#007AFF]"/>
            {aiQ.data?.enabled_models?.length || 0} enabled
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {(aiQ.data?.models || []).map((model) => (
            <button
              key={model.id}
              type="button"
              onClick={() => toggleModel(model.id)}
              disabled={saveAI.isPending || !model.configured}
              data-testid={`ai-model-toggle-${model.id}`}
              className={`p-4 border text-left transition-colors disabled:opacity-40 ${
                model.enabled
                  ? "border-emerald-500/45 bg-emerald-500/5"
                  : "border-white/10 hover:border-white/30"
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <Zap className={`w-4 h-4 ${model.enabled ? "text-emerald-400" : "text-zinc-600"}`}/>
                <span className={`text-[9px] font-mono uppercase tracking-widest ${
                  model.enabled ? "text-emerald-400" : model.configured ? "text-zinc-500" : "text-[#FF3B30]"
                }`}>
                  {model.enabled ? "ON" : model.configured ? "OFF" : "NO KEY"}
                </span>
              </div>
              <div className="text-[13px] font-medium text-zinc-100">{model.label}</div>
              <div className="text-[10px] font-mono text-zinc-500 mt-1">
                {model.provider === "anthropic" ? "Claude" : "Google"} · {model.tier}
              </div>
            </button>
          ))}
        </div>
        <div className="text-[11px] text-zinc-500 mt-3">
          At least one configured model must remain enabled. API keys stay server-side and are never sent to the browser.
        </div>
      </div>

      {/* Webhook URL */}
      <div className="border border-white/10 p-6 mb-6">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">// syften webhook endpoint</div>
        <h3 className="font-display font-black text-2xl tracking-tight mb-4">Paste this URL into Syften.</h3>
        <div className="bg-[#050505] border border-white/10 p-4 font-mono text-[13px] break-all text-zinc-100 mb-3" data-testid="webhook-url-display">
          {q.isLoading ? "…" : full}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={copy} data-testid="copy-webhook-btn" className="border border-white/15 hover:border-white/30 px-3 py-2 text-[12px] font-medium flex items-center gap-2">
            <Copy className="w-4 h-4"/> Copy URL
          </button>
          <div className="text-[12px] text-zinc-500 font-mono">Method: <span className="text-zinc-200">POST</span> · Content-Type: <span className="text-zinc-200">application/json</span></div>
        </div>
      </div>

      {/* Syften REST API */}
      <div className="border border-white/10 p-6 mb-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">// syften rest api</div>
            <h3 className="font-display font-black text-2xl tracking-tight">Direct API integration.</h3>
            <p className="text-[13px] text-zinc-400 max-w-lg mt-2">
              Verify the connection or backfill your queue by pulling recent matches directly from Syften — bypassing the webhook.
            </p>
          </div>
          <div className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border shrink-0 ${apiConfigured ? "border-emerald-500/40 text-emerald-400" : "border-[#FF3B30]/40 text-[#FF3B30]"}`}>
            {apiConfigured ? "KEY CONFIGURED" : "KEY MISSING"}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Test connection */}
          <div className="border border-white/10 p-4 bg-[#0a0a0c]">
            <div className="flex items-center gap-2 mb-3">
              <PlugZap className="w-4 h-4 text-[#007AFF]"/>
              <div className="font-medium text-white">Test connection</div>
            </div>
            <p className="text-[12px] text-zinc-500 leading-relaxed mb-4">
              Verifies your API key and returns account plan, quota counters, and current filter list.
            </p>
            <button
              onClick={() => testSyften.mutate()}
              disabled={testSyften.isPending || !apiConfigured}
              data-testid="syften-test-btn"
              className="w-full border border-white/15 hover:border-white/40 px-3 py-2 text-[12px] font-medium flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {testSyften.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <PlugZap className="w-4 h-4"/>}
              RUN TEST
            </button>
          </div>

          {/* Pull matches */}
          <div className="border border-white/10 p-4 bg-[#0a0a0c]">
            <div className="flex items-center gap-2 mb-3">
              <DownloadCloud className="w-4 h-4 text-[#FF3B30]"/>
              <div className="font-medium text-white">Pull recent matches</div>
            </div>
            <p className="text-[12px] text-zinc-500 leading-relaxed mb-3">
              Fetches the latest matches from Syften and runs them through the same intent-scoring pipeline. Dedupes by URL.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
              <input
                type="number" min={1} max={100}
                value={pullLimit}
                onChange={(e)=>setPullLimit(parseInt(e.target.value || "1", 10))}
                data-testid="syften-pull-limit"
                placeholder="Limit"
                className="col-span-1 bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px] font-mono"
              />
              <input
                type="text"
                value={pullFilter}
                onChange={(e)=>setPullFilter(e.target.value)}
                data-testid="syften-pull-filter"
                placeholder="Filter (optional, exact match)"
                className="col-span-2 bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px] font-mono"
              />
            </div>
            <button
              onClick={() => pullSyften.mutate()}
              disabled={pullSyften.isPending || !apiConfigured}
              data-testid="syften-pull-btn"
              className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium px-3 py-2 text-[12px] flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {pullSyften.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <DownloadCloud className="w-4 h-4"/>}
              PULL &amp; INGEST
            </button>
          </div>
        </div>

        {/* Test result */}
        {testResult && (
          <div className="mt-4 border border-white/10 p-4 bg-[#050505]" data-testid="syften-test-result">
            {testResult.error ? (
              <div className="flex items-start gap-2 text-[#FF3B30]">
                <XCircle className="w-4 h-4 mt-0.5"/>
                <div className="font-mono text-[12px]">{testResult.error}</div>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-2 mb-4">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5"/>
                  <div>
                    <div className="text-[13px] text-zinc-100 font-medium">{testResult.info?.name}</div>
                    <div className="font-mono text-[11px] text-zinc-500">
                      {testResult.info?.plan?.Name} · matches used: {testResult.info?.stats?.matches ?? 0} / {testResult.info?.plan?.DailyMatches}/day
                    </div>
                  </div>
                </div>
                {Array.isArray(testResult.filters) && testResult.filters.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
                      Active filters ({testResult.filters.filter(f => f && !f.startsWith("//")).length})
                    </div>
                    <div className="max-h-56 overflow-y-auto bg-[#09090B] border border-white/5 p-3 font-mono text-[11px] leading-relaxed">
                      {testResult.filters.map((f, i) => (
                        <div key={`line-${i}-${(f||'').slice(0,20)}`} className={f?.startsWith("//") ? "text-zinc-600 italic" : "text-zinc-300"}>
                          {f || <span className="text-zinc-700">—</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Payload contract */}
      <div className="border border-white/10 p-6 mb-6">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">// payload contract</div>
        <div className="font-mono text-[12px] text-zinc-500 whitespace-pre">
{`[
  {
    "item_url": "https://reddit.com/...",
    "backend_sub": "r/startups",
    "author": "user123",
    "title": "...",
    "text": "...",
    "filter": "keyword",
    "analysis": { "sentiment": "positive" }
  }
]`}
        </div>
        <div className="mt-3 text-[12px] text-zinc-500">Threads scoring &gt; 0.75 commercial intent land in your High-Priority Queue. Others are archived silently. 200 OK returned instantly for Syften retry logic.</div>
      </div>

      {/* Drift (daily) */}
      <div className="border border-white/10 p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">// drift protocol (a.k.a. karma)</div>
            <h3 className="font-display font-black text-2xl tracking-tight">Manual daily assignment.</h3>
            <p className="text-[13px] text-zinc-400 max-w-lg mt-2">Assigns two benign engagement tasks (upvote / neutral comment on non-branded subreddits) to every active agent to preserve organic behavior signals.</p>
          </div>
          <button
            onClick={() => karma.mutate()}
            disabled={karma.isPending}
            data-testid="karma-generate-btn"
            className="bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium px-4 py-2.5 flex items-center gap-2 disabled:opacity-50"
          >
            {karma.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
            Trigger Drift Batch
          </button>
        </div>
      </div>
    </Layout>
  );
}
